import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, RefObject } from 'react'
import { useHostFormat } from '../i18n/hostFormat'
import { formatOdds, type OddsFormat } from '../utils/odds'
import { brandingToCssVars, DEFAULT_BRANDING, resolveBrandingWithOverrides, type BrandingConfig } from '../branding'
import { SurfaceHeadingView } from '../components/SurfaceHeading'
import headingStyles from '../components/SurfaceHeading.css?inline'
import surfaceStyles from './PrepacksSurface.css?inline'
import { artworkColorScheme, artworkVariables, buildSrcDoc, deriveBetType, betTypeLabel, legBetLabel, NATIVE_COLOR_VARIABLES, packTypeLabel, resolveCardVar, sanitizePublicTemplate, TAG_STYLE_FILLED, TAG_STYLE_VARIABLE } from '../../../shared/pkg/prepacks/templateRendering.js'
import { readableTextOn } from '../../../shared/pkg/prepacks/colorMath.js'
import { getFallbackTemplate, getTemplateVariant, normalizeTemplates } from '../../../shared/pkg/prepacks/templateVariants.js'
import { BANNER_FILL_VARIABLE, CARD_PICTURE_BACKGROUND, CARD_PICTURE_FOOTER, hasFullCardPicture, templateWithBannerFill } from '../../../shared/pkg/prepacks/bannerFills.js'
import { patchFrameDocument } from '../utils/patchFrameDocument'
import { useFeedConfig } from '../contexts/feedConfigContext'
import type { PrepackFormProps, PrepackForms } from './PrepackForm'
import { groupPrepacks, prepackCopiesLabel, prepackPayoutLabel, resolveExampleStake, sportGlyph, type PublicPrepack, type PrepackCardTemplate, type PrepackSurfaceLayoutConfig } from './prepackRuntimeModel'

const CSS_COLOR_PATTERN = /^(?:#[0-9a-f]{3}|#[0-9a-f]{4}|#[0-9a-f]{6}|#[0-9a-f]{8}|(?:rgba?|hsla?)\([^)]*\))$/i

const TEMPLATE_DESIGN_WIDTH = 380
// Every card, native or template artwork, lists this many legs before
// offering "See all N bets". Artwork is designed around three legs, so a
// six-leg pack takes the same slot as a three-leg one until it is expanded.
const COLLAPSED_LEG_COUNT = 3
// The opacity "See all N bets" is painted at under template artwork (PrepacksSurface.css,
// --prepack-card-rows-text-opacity's default): its text color is judged as painted.
const ROWS_TEXT_OPACITY = 0.85
// Template artwork carries its own odds, so its action reads as a plain call to action.
const TEMPLATE_CTA_LABEL = 'Tail it!'
const TEMPLATE_FALLBACK_HEIGHT = 280
const DEFAULT_CARD_TEMPLATE = getFallbackTemplate([])
function UnavailableForm({ children }: PrepackFormProps) { return <>{children({})}</> }
const PrepackForm = lazy(() => import('./PrepackForm').catch(() => ({ default: UnavailableForm })))

// The artwork's card, the node useFrameFill stretches.
function artworkCard(doc: Document) {
  return doc.querySelector<HTMLElement>('.card')
}

// The node whose height the frame follows: the card when the template has one.
function measuredNode(doc: Document) {
  return artworkCard(doc) || doc.body
}

function setMinHeight(node: HTMLElement, value: string) {
  if (value) node.style.setProperty('min-height', value)
  else node.style.removeProperty('min-height')
}

// The card's inline min-height, shared by useFrameFill and
// useFrameContentHeight: the one the template wrote (kept, and put back when
// the card is no longer stretched) and the one useFrameFill left on it.
type ArtworkFill = { authored: string; applied: string }

// Scale that fits the fixed-width design into the wrapper's width, and the
// height the wrapper is given: a carousel or grid row stretches it to the
// tallest card's (#1039).
function useWrapperBox(wrapperRef: RefObject<HTMLDivElement | null>) {
  const [box, setBox] = useState({ scale: 1, slotHeight: 0 })
  useEffect(() => {
    const element = wrapperRef.current
    if (!element) return
    const update = () => {
      const scale = (element.offsetWidth || TEMPLATE_DESIGN_WIDTH) / TEMPLATE_DESIGN_WIDTH
      const slotHeight = element.offsetHeight
      setBox((current) => (current.scale === scale && current.slotHeight === slotHeight ? current : { scale, slotHeight }))
    }
    update()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(update)
    observer.observe(element)
    return () => observer.disconnect()
  }, [wrapperRef])
  return box
}

// Natural height of the frame's content (see measuredNode), kept current by a
// ResizeObserver on that node. A card stretched to its row (useFrameFill) is
// read with the template's own min-height back in place of the stretch, so it
// never measures the stretch back as its own height.
function useFrameContentHeight(frameRef: RefObject<HTMLIFrameElement | null>, fill: RefObject<ArtworkFill>) {
  const contentObserver = useRef<ResizeObserver | null>(null)
  const observedTarget = useRef<Element | null>(null)
  const [height, setHeight] = useState(TEMPLATE_FALLBACK_HEIGHT)
  useEffect(() => () => contentObserver.current?.disconnect(), [])
  const measureContent = useCallback(() => {
    const doc = frameRef.current?.contentDocument
    if (!doc?.body) return
    contentObserver.current?.disconnect()
    if (!artworkCard(doc)) {
      doc.documentElement.style.setProperty('min-height', '0', 'important')
      doc.body.style.setProperty('min-height', '0', 'important')
    }
    const target = measuredNode(doc)
    observedTarget.current = target
    const update = () => {
      const node = target as HTMLElement
      const { authored, applied } = fill.current
      const stretched = node !== doc.body && applied !== authored && node.style.getPropertyValue('min-height') === applied
      if (stretched) setMinHeight(node, authored)
      const measured = node.getBoundingClientRect().height
      if (stretched) setMinHeight(node, applied)
      if (measured > 0) setHeight(Math.ceil(measured))
    }
    update()
    if (typeof ResizeObserver === 'undefined') return
    contentObserver.current = new ResizeObserver(update)
    contentObserver.current.observe(target)
  }, [frameRef, fill])
  // After a patch an active observer already reports size changes of the same
  // node; measure again if the patch replaced it or nothing is observing it
  // (no ResizeObserver in this browser).
  const remeasureIfReplaced = useCallback(() => {
    const doc = frameRef.current?.contentDocument
    if (doc && (!contentObserver.current || measuredNode(doc) !== observedTarget.current)) measureContent()
  }, [frameRef, measureContent])
  return { height, measureContent, remeasureIfReplaced }
}

// Stretches the artwork's .card to fillHeight design pixels (0: the template's
// own min-height, if it wrote one), so its colour, gradient or picture runs
// down to the footer. Returns the step that applies it, to run again after
// every load and patch: patchFrameDocument resets inline styles to the new
// document's. A stretch is always taller than the template's min-height, since
// the natural height includes it. Artwork without a .card is left alone.
function useFrameFill(frameRef: RefObject<HTMLIFrameElement | null>, fill: RefObject<ArtworkFill>, fillHeight: number) {
  const latest = useRef(fillHeight)
  const apply = useCallback(() => {
    const doc = frameRef.current?.contentDocument
    const card = doc && artworkCard(doc)
    if (!card) return
    const current = card.style.getPropertyValue('min-height')
    // Anything other than what was left here came with the document.
    if (current !== fill.current.applied) fill.current.authored = current
    const value = latest.current > 0 ? `${latest.current}px` : fill.current.authored
    fill.current.applied = value
    if (current !== value) setMinHeight(card, value)
  }, [frameRef, fill])
  useLayoutEffect(() => {
    latest.current = fillHeight
    apply()
  }, [apply, fillHeight])
  return apply
}

// Keeps a srcdoc frame on the latest document. The frame navigates once; later
// documents (title, odds, legs, colours) are patched into the loaded one so it
// doesn't blank and reload each time. With nothing patchable loaded (first
// load in flight, or a failed patch) it navigates to the latest instead.
function useLiveFrameDocument(
  srcDoc: string,
  frameRef: RefObject<HTMLIFrameElement | null>,
  { onLoad, onPatch }: { onLoad: () => void; onPatch: () => void },
) {
  const [frameDoc, setFrameDoc] = useState(srcDoc)
  const latestDoc = useRef(srcDoc)
  // What the frame shows; null until a document with content has loaded.
  const displayedDoc = useRef<string | null>(null)
  const showLatest = useCallback(() => {
    const html = latestDoc.current
    if (displayedDoc.current === html) return
    if (displayedDoc.current !== null && patchFrameDocument(frameRef.current, html)) {
      displayedDoc.current = html
      onPatch()
      return
    }
    displayedDoc.current = null
    setFrameDoc(html)
  }, [frameRef, onPatch])
  useLayoutEffect(() => {
    latestDoc.current = srcDoc
    showLatest()
  }, [showLatest, srcDoc])
  const handleLoad = useCallback(() => {
    const frame = frameRef.current
    displayedDoc.current = frame?.contentDocument?.body?.childNodes.length ? frame.getAttribute('srcdoc') : null
    onLoad()
    showLatest()
  }, [frameRef, onLoad, showLatest])
  return { frameDoc, handleLoad }
}

function TemplateCardFrame({ template, pack, colorMode, exampleStake, visibleLegCount, oddsFormat, currency, locale, legForms }: { template: PrepackCardTemplate; pack: PublicPrepack; colorMode: 'light' | 'dark'; exampleStake: number; visibleLegCount: number; oddsFormat: OddsFormat; currency?: string; locale?: string; legForms: string[] }) {
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const frameRef = useRef<HTMLIFrameElement | null>(null)
  const { scale, slotHeight } = useWrapperBox(wrapperRef)
  const fill = useRef<ArtworkFill>({ authored: '', applied: '' })
  const { height, measureContent, remeasureIfReplaced } = useFrameContentHeight(frameRef, fill)
  // The slot the row gives this artwork, in design pixels; a card alone measures its own height back, within a pixel
  // of rounding, and keeps it.
  const slot = Math.ceil(slotHeight / scale)
  // The slot is read in whole pixels (offsetHeight) and the real one may be half a pixel taller, so a stretched frame
  // takes one more to cover it; the wrapper clips the rest.
  const frameHeight = slot > height + 1 ? slot + 1 : height
  const applyFill = useFrameFill(frameRef, fill, frameHeight > height ? frameHeight : 0)
  const onLoad = useCallback(() => { measureContent(); applyFill() }, [measureContent, applyFill])
  const onPatch = useCallback(() => { remeasureIfReplaced(); applyFill() }, [remeasureIfReplaced, applyFill])
  const sanitizedTemplate = useMemo(() => sanitizePublicTemplate(template), [template])
  const frameScheme = useMemo(() => artworkColorScheme(template, colorMode), [template, colorMode])
  const srcDoc = useMemo(() => buildSrcDoc(sanitizedTemplate, pack.template_vars, undefined, pack, { colorMode, publicCard: true, exampleStake, visibleLegCount, oddsFormat, currency, locale, legForms }), [pack, sanitizedTemplate, colorMode, exampleStake, visibleLegCount, oddsFormat, currency, locale, legForms])
  const { frameDoc, handleLoad } = useLiveFrameDocument(srcDoc, frameRef, { onLoad, onPatch })
  // At least the artwork's own height; it grows into the slot a carousel or grid row gives the card (#1039), and the
  // frame and its artwork follow it there.
  return (
    <div ref={wrapperRef} className="prepack-widget-card__frame-wrap" style={{ minHeight: `${Math.round(height * scale)}px` }}>
      <iframe ref={frameRef} onLoad={handleLoad} srcDoc={frameDoc} sandbox="allow-same-origin" scrolling="no"
        className="prepack-widget-card__iframe" tabIndex={-1}
        // The frame wears the scheme its artwork declares: one of another scheme gets an opaque canvas, and artwork
        // that lets the card's own picture show through (#1038) needs a transparent one.
        style={{ width: `${TEMPLATE_DESIGN_WIDTH}px`, height: `${frameHeight}px`, transform: `scale(${scale})`, transformOrigin: 'top left', colorScheme: frameScheme }}
        title={pack.title || template.name} />
    </div>
  )
}

// Operator tags (Studio's "Tags (n/5)": at most five, trimmed and deduplicated
// by the server, NormalizePrepackTags) as a row of chips, quiet or, when the pack's operator chose
// it, filled. The row belongs to the native shell, so every card carries it
// the same way, native or artwork, and Studio previews show what the widget
// ships.
function PrepackTagList({ tags, filled, className }: { tags: string[]; filled: boolean; className: string }) {
  if (tags.length === 0) return null
  return (
    <ul className={['prepack-widget-card__tags', filled ? 'prepack-widget-card__tags--filled' : '', className].filter(Boolean).join(' ')} aria-label="Tags">
      {tags.map((tag, index) => <li key={`${tag}-${index}`} className="prepack-widget-card__tag">{tag}</li>)}
    </ul>
  )
}

export interface PrepackCardViewProps {
  pack: PublicPrepack
  template?: PrepackCardTemplate | null
  branding?: BrandingConfig
  colorMode?: 'light' | 'dark'
  ctaLabel?: string
  showOddsBadge?: boolean
  exampleStake?: number
  oddsFormat?: OddsFormat
  readOnly?: boolean
  preview?: boolean
  actionState?: 'idle' | 'loading' | 'success' | 'error'
  errorMessage?: string | null
  onAction?: (pack: PublicPrepack) => void
  observeCard?: (node: HTMLElement | null) => void | (() => void)
  position?: number
}

// Standalone consumers load PrepacksSurface.css once in their own styling root.
export function PrepackCardView(props: PrepackCardViewProps) {
  const config = useFeedConfig()
  const card = <PrepackCardContent {...props} />
  if (props.preview || config.loading || config.statContextEnabled !== true || !config.apiUrl || !config.organization) return card
  return (
    <Suspense fallback={card}>
      <PrepackForm pack={props.pack} apiBase={config.apiUrl} organization={config.organization}>
        {(forms) => <PrepackCardContent {...props} forms={forms} />}
      </PrepackForm>
    </Suspense>
  )
}

function PrepackCardContent({ pack, template, ctaLabel = 'Add to Betslip', showOddsBadge = true, exampleStake,
  oddsFormat = 'american', branding, colorMode: colorModeProp, readOnly = false, preview = false, actionState = 'idle', errorMessage, onAction, observeCard, position, forms }: PrepackCardViewProps & { forms?: PrepackForms }) {
  // A pack whose schedule chose another picture fill than its template's (issue 683) is drawn in that fill.
  const packFill = pack.template_vars?.[BANNER_FILL_VARIABLE]
  const activeTemplate = useMemo(() => templateWithBannerFill(template || DEFAULT_CARD_TEMPLATE, packFill), [template, packFill])
  const colorMode = colorModeProp ?? ((branding ?? DEFAULT_BRANDING).darkMode ? 'dark' : 'light')
  const usesTemplateArtwork = Boolean(activeTemplate?.htmlCode) && !activeTemplate.isFallback
  // Artwork that runs its whole-card picture under the footer (#1038, IMAGE_CARD_FULL_CSS) paints no picture and no
  // color itself: the card draws the picture under the scrim from the same variables the artwork reads, and its frame
  // and footer paint nothing of their own (PrepacksSurface.css, data-full-picture). Read from its marker, which a hand
  // edit of the other rules keeps.
  const fullPicture = useMemo(() => usesTemplateArtwork && hasFullCardPicture(activeTemplate.cssCode), [activeTemplate, usesTemplateArtwork])
  const { style, scrimFooter } = useMemo(() => {
    const variant = getTemplateVariant(activeTemplate, colorMode)
    const resolved = resolveBrandingWithOverrides(branding ?? DEFAULT_BRANDING, variant.branding)
    // A standalone card without a branding prop still inherits the host's
    // untouched tokens; only explicit variant values belong on the article.
    const vars = brandingToCssVars(resolved, branding ? undefined : variant.branding)
    // A pack's values as the artwork takes them: a removed picture (banner_hidden) is none, an unsafe value is dropped.
    for (const [key, value] of artworkVariables(activeTemplate, colorMode, pack.template_vars)) vars[key] = value
    for (const variable of NATIVE_COLOR_VARIABLES) {
      const value = pack.template_vars?.[variable.key]
      if (typeof value === 'string' && CSS_COLOR_PATTERN.test(value.trim())) {
        vars[variable.key] = value.trim()
      }
    }
    if (fullPicture) vars['--prepack-card-picture'] = CARD_PICTURE_BACKGROUND
    // The rows the shell draws between the artwork and the action bar ("See all N bets", the tag row) continue the
    // footer on every artwork card (PrepacksSurface.css, issues 1039 and 1092), so their text has to read on the
    // footer's color, which a palette, an operator's "Footer background" or a pack's own value sets. On the scrim (a
    // card picture's tinted footer, CARD_PICTURE_FOOTER, or the transparent footer of a full picture) that is the
    // header text the scrim guarantees. Otherwise the card's heading and subheading tokens when they reach 4.5:1 on the
    // resolved footer as painted ("See all" at ROWS_TEXT_OPACITY; a quiet chip's text plain, its 12% fill being the
    // shell's, as on the native card), and when they do not white, #111111 or black, the softest that reaches 4.5:1
    // plain, painted plain (no opacity, no chip fill). Nothing is set (the CSS keeps those tokens) when either color is
    // not a plain one this can read (a gradient, color-mix, transparency).
    const scrimFooter = usesTemplateArtwork && (fullPicture || vars['--feed-component-prepack-actions-bg'] === CARD_PICTURE_FOOTER)
    if (scrimFooter) {
      vars['--prepack-card-rows-text'] = 'var(--header-text, var(--feed-heading, #ffffff))'
      vars['--prepack-card-rows-muted'] = 'var(--header-text, var(--feed-heading, #ffffff))'
    } else if (usesTemplateArtwork) {
      const footer = resolveCardVar(vars['--feed-component-prepack-actions-bg'] ?? 'var(--feed-surface)', vars)
      const rowsBackground = footer && footer !== 'transparent' ? footer : resolveCardVar('var(--feed-surface)', vars)
      for (const [property, token, paintProperty, plain, paint] of [
        ['--prepack-card-rows-text', '--feed-heading', '--prepack-card-rows-text-opacity', '1', { opacity: ROWS_TEXT_OPACITY }],
        ['--prepack-card-rows-muted', '--feed-subheading', '--prepack-card-rows-muted-tint', '0%', {}],
      ] as const) {
        const preferred = resolveCardVar(`var(${token})`, vars)
        const readable = rowsBackground && preferred ? readableTextOn(rowsBackground, preferred, paint) : undefined
        if (!readable) continue
        if (readable.kept) {
          vars[property] = `var(${token})`
          continue
        }
        vars[property] = readable.color
        vars[paintProperty] = plain
      }
    }
    return { style: vars as CSSProperties, scrimFooter }
  }, [activeTemplate, branding, colorMode, fullPicture, pack.template_vars, usesTemplateArtwork])
  const loading = actionState === 'loading'
  const success = actionState === 'success'
  const error = actionState === 'error'
  const visibleBetType = pack.bet_type !== undefined ? pack.bet_type : deriveBetType(pack.legs)
  const disabled = readOnly || loading || preview || !onAction
  const { currency, locale } = useHostFormat()
  const payoutLabel = prepackPayoutLabel(pack, resolveExampleStake(exampleStake), { currency, locale })
  const ctaNote = loading ? 'Adding...' : success ? 'Added!' : readOnly ? 'View Only' : payoutLabel
  const templateActionIsLabel = usesTemplateArtwork && activeTemplate.nativeAction !== 'odds'
  // The bets counter belongs to the native card only; template artwork keeps its own composition.
  const copiesLabel = usesTemplateArtwork ? '' : prepackCopiesLabel(pack.copies)
  const typeChip = (visibleBetType ? betTypeLabel(visibleBetType, oddsFormat) : '') || packTypeLabel(pack.pack_type, oddsFormat)
  // Keep US cards byte-identical: an absent American quote is an em dash, never a multiplier-derived price.
  const quotedOdds = oddsFormat === 'decimal' ? formatOdds({ american: pack.odds, decimal: pack.payout_multiplier }, oddsFormat) : (pack.odds || '—')
  const legs = useMemo(() => pack.legs ?? [], [pack.legs])
  const legForms = useMemo(() => legs.map((_, index) => forms?.[index]?.text ?? ''), [legs, forms])
  const subtitle = typeof pack.subtitle === 'string' ? pack.subtitle.trim() : ''
  const tags = pack.tags ?? []
  const filledTags = pack.template_vars?.[TAG_STYLE_VARIABLE] === TAG_STYLE_FILLED
  const [legsExpanded, setLegsExpanded] = useState(false)
  const visibleLegs = legsExpanded || legs.length <= COLLAPSED_LEG_COUNT ? legs : legs.slice(0, COLLAPSED_LEG_COUNT)
  const hiddenLegCount = legs.length - visibleLegs.length
  const releaseRef = useRef<(() => void) | undefined>()
  const observationRef = useCallback((node: HTMLElement | null) => {
    releaseRef.current?.()
    releaseRef.current = observeCard?.(node) || undefined
  }, [observeCard])
  const seeAllLegs = hiddenLegCount > 0 ? (
    <button type="button" className="prepack-widget-card__fallback-more" onClick={() => setLegsExpanded(true)} aria-expanded={false}>
      See all {legs.length} bets
      <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3.5 6l4.5 4.5L12.5 6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
    </button>
  ) : null
  return (
    <article ref={observeCard ? observationRef : undefined} className="prepack-widget-card" style={style} data-color-mode={colorMode} data-full-picture={fullPicture ? 'true' : undefined} data-scrim-footer={scrimFooter ? 'true' : undefined} data-prepack-id={observeCard ? pack.id : undefined} data-position={observeCard ? position : undefined} data-has-copies={copiesLabel ? 'true' : undefined} data-native-action={usesTemplateArtwork ? (templateActionIsLabel ? 'label' : 'odds') : undefined} aria-label={pack.title} data-testid={`prepack-card-${pack.id}`}>
      {usesTemplateArtwork ? (
        <>
          <TemplateCardFrame template={activeTemplate} pack={pack} colorMode={colorMode} exampleStake={resolveExampleStake(exampleStake)} visibleLegCount={visibleLegs.length} oddsFormat={oddsFormat} currency={currency} locale={locale} legForms={legForms} />
          {seeAllLegs ? <div className="prepack-widget-card__frame-more">{seeAllLegs}</div> : null}
          <PrepackTagList tags={tags} filled={filledTags} className="prepack-widget-card__frame-tags" />
        </>
      ) : (
        <div className="prepack-widget-card__fallback">
          <div className="prepack-widget-card__fallback-header">
            <span className="prepack-widget-card__fallback-type">{typeChip}</span>
            <h4 className="prepack-widget-card__fallback-title">{pack.title}</h4>
          </div>
          {subtitle ? <p className="prepack-widget-card__fallback-subtitle">{subtitle}</p> : null}
          <PrepackTagList tags={tags} filled={filledTags} className="prepack-widget-card__fallback-tags" />
          <ul className="prepack-widget-card__fallback-legs">
            {visibleLegs.map((leg, idx) => {
              const event = leg.event_label || leg.event || ''
              return (
                <li key={leg.id || leg.outcome_id || idx} className="prepack-widget-card__fallback-leg" data-has-form={forms?.[idx] ? 'true' : undefined}>
                  <span className="prepack-widget-card__fallback-sport" aria-hidden="true">{sportGlyph(leg.sport || pack.sport, leg.league || pack.league || '')}</span>
                  <span className="prepack-widget-card__fallback-selection">{leg.participant || leg.selection_name || 'Selection'}</span>
                  <span className="prepack-widget-card__fallback-market">{legBetLabel(leg, oddsFormat) || 'Market'}</span>
                  {forms?.[idx]?.chip}
                  {event ? <span className="prepack-widget-card__fallback-event">({String(event)})</span> : null}
                </li>
              )
            })}
          </ul>
          {seeAllLegs}
        </div>
      )}
      <div className="prepack-widget-card__action-bar">
        <button type="button"
          className={`prepack-widget-card__cta ${success ? 'prepack-widget-card__cta--success' : ''} ${error ? 'prepack-widget-card__cta--error' : ''} ${preview && !readOnly && !loading ? 'prepack-widget-card__cta--preview' : ''}`}
          disabled={disabled} aria-busy={loading || undefined}
          onClick={() => { if (!disabled) onAction?.(pack) }} aria-label={`${ctaLabel} for ${pack.title}`}>
          {templateActionIsLabel ? (
            <span className="prepack-widget-card__cta-label">{loading ? 'Adding...' : success ? 'Added!' : readOnly ? 'View Only' : oddsFormat === 'decimal' ? ctaLabel : TEMPLATE_CTA_LABEL}</span>
          ) : (
            <>
              {showOddsBadge
                ? <span className="prepack-widget-card__odds-badge" data-testid="quoted-odds">{quotedOdds}</span>
                : <span className="prepack-widget-card__cta-label">{ctaLabel}</span>}
              {ctaNote ? <span className="prepack-widget-card__cta-note">{ctaNote}</span> : null}
            </>
          )}
        </button>
      </div>
      {copiesLabel ? (
        <span className="prepack-widget-card__copies" data-testid="copy-count">
          <span className="prepack-widget-card__copies-flame" aria-hidden="true">{'\ud83d\udd25'}</span>
          {copiesLabel}
        </span>
      ) : null}
      {error && errorMessage ? <div className="prepack-widget-card__feedback" role="alert">{errorMessage}</div> : null}
    </article>
  )
}

interface PresentationSurface {
  name?: string
  display_type?: string
  layout_config?: PrepackSurfaceLayoutConfig
  config?: Record<string, unknown> | null
}
export interface PrepacksSurfaceViewProps {
  items: PublicPrepack[]
  templates?: PrepackCardTemplate[]
  layout?: PrepackSurfaceLayoutConfig
  displayType?: string
  surface?: PresentationSurface | null
  branding?: BrandingConfig
  oddsFormat?: OddsFormat
  readOnly?: boolean
  preview?: boolean
  actionLoadingId?: string | null
  actionSuccessId?: string | null
  actionErrorId?: string | null
  actionErrorMessage?: string | null
  onAction?: (pack: PublicPrepack) => void
  observeCard?: (node: HTMLElement | null) => void | (() => void)
}

const EMPTY_LAYOUT: PrepackSurfaceLayoutConfig = {}

export function PrepacksSurfaceView({ items, templates, layout: layoutProp, displayType: displayTypeProp, surface, branding, oddsFormat = 'american',
  readOnly = false, preview = false, actionLoadingId, actionSuccessId, actionErrorId, actionErrorMessage, onAction, observeCard }: PrepacksSurfaceViewProps) {
  const layout = layoutProp ?? surface?.layout_config ?? EMPTY_LAYOUT
  const displayType = displayTypeProp ?? surface?.display_type ?? 'carousel'
  const isGrid = displayType === 'grid'
  const isStory = displayType === 'story_block' || displayType === 'vertical_stories'
  const isCarousel = !isGrid && !isStory
  const trackRef = useRef<HTMLDivElement | null>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const dimension = layout.dimension && layout.dimension !== 'none' ? layout.dimension : null
  const groups = useMemo(() => {
    const limit = layout.max_display_count
    const visibleItems = typeof limit === 'number' && limit > 0 ? items.slice(0, limit) : items
    return groupPrepacks(visibleItems, dimension)
  }, [items, dimension, layout.max_display_count])
  const catalog = useMemo(() => normalizeTemplates(templates), [templates])
  const fallbackTemplate = useMemo(() => getFallbackTemplate(catalog), [catalog])
  const templatesMap = useMemo(() => {
    const map = new Map<string, PrepackCardTemplate>()
    for (const template of catalog) map.set(template.id, template)
    return map
  }, [catalog])
  const style = useMemo(() => {
    if (!branding) return undefined
    const vars = brandingToCssVars(branding)
    vars['--feed-border-width'] = branding.borderWidth
    return vars as CSSProperties
  }, [branding])
  const checkScroll = useCallback(() => {
    const el = trackRef.current
    setCanScrollLeft(Boolean(el && el.scrollLeft > 2))
    setCanScrollRight(Boolean(el && el.scrollLeft < el.scrollWidth - el.clientWidth - 2))
  }, [])
  useEffect(() => {
    checkScroll()
    const el = trackRef.current
    if (!el) return
    el.addEventListener('scroll', checkScroll, { passive: true })
    window.addEventListener('resize', checkScroll)
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(checkScroll)
    observer?.observe(el)
    for (const child of el.children) observer?.observe(child)
    return () => {
      el.removeEventListener('scroll', checkScroll)
      window.removeEventListener('resize', checkScroll)
      observer?.disconnect()
    }
  }, [checkScroll, groups, displayType])
  const scrollByAmount = (direction: 'left' | 'right') => {
    const el = trackRef.current
    if (!el) return
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    el.scrollBy({ left: el.clientWidth * 0.75 * (direction === 'left' ? -1 : 1), behavior: reducedMotion ? 'auto' : 'smooth' })
  }
  const title = typeof surface?.config?.title === 'string' ? surface.config.title : surface?.name || 'Prepacks'
  if (!items.length) return null
  return (
    <div className={`prepacks-surface prepacks-surface--${isGrid ? 'grid' : isStory ? 'story' : 'carousel'} prepacks-surface--density-${layout.card_density || 'comfortable'}`}
      style={style} data-testid="prepacks-surface">
      <style>{surfaceStyles}{headingStyles}</style>
      <div className="prepacks-surface__header">
        <SurfaceHeadingView title={title} highlight={surface?.config?.title_highlight} />
        {isCarousel && <div className="prepacks-surface__controls">
          <button type="button" className="prepacks-surface__nav-btn" disabled={!canScrollLeft} onClick={() => scrollByAmount('left')} aria-label="Previous prepacks">‹</button>
          <button type="button" className="prepacks-surface__nav-btn" disabled={!canScrollRight} onClick={() => scrollByAmount('right')} aria-label="Next prepacks">›</button>
        </div>}
      </div>
      {groups.map((group, groupIdx) => (
        <div key={group.id || group.name || groupIdx} className="prepacks-group">
          {group.name ? <h3 className="prepacks-group__heading">{group.name}</h3> : null}
          <div className="prepacks-surface__track" ref={groupIdx === 0 && isCarousel ? trackRef : undefined}>
            {group.items.map((pack, packIdx) => <PrepackCardView key={pack.id} pack={pack}
              template={pack.template_id ? templatesMap.get(pack.template_id) || fallbackTemplate : fallbackTemplate}
              branding={branding} colorMode={branding?.darkMode === false ? 'light' : 'dark'} oddsFormat={oddsFormat}
              ctaLabel={layout.cta_label || branding?.buttonText || 'Add to Betslip'} showOddsBadge={layout.show_odds_badge !== false} exampleStake={layout.example_stake}
              readOnly={readOnly} preview={preview}
              actionState={actionLoadingId === pack.id ? 'loading' : actionErrorId === pack.id ? 'error' : actionSuccessId === pack.id ? 'success' : 'idle'}
              errorMessage={actionErrorId === pack.id ? actionErrorMessage : null} onAction={onAction} observeCard={observeCard} position={groups.slice(0, groupIdx).reduce((total, prior) => total + prior.items.length, 0) + packIdx} />)}
          </div>
        </div>
      ))}
    </div>
  )
}
